import type { MutableState } from '../State';
import {
  getDecorationItem,
  getDecorationItemTier,
  getSelectedItemByID,
} from './decoration.getters';
import {
  DecorationRoomID,
  DecorationItemID,
  DecorationSlot,
  DecorationTierID,
  tiersItemsMap,
} from './decoration.ruleset';
import * as Inventory from '../inventory';
import { assertNever } from '../utils';
import { CurrencyConsumeFeature } from '../analytics/events';
import { CurrencyReplicantAPI } from '../inventory/inventory.utils';

/**
 * Purchase one decoration item
 */
export function purchaseDecorationItem(
  state: MutableState,
  id: DecorationItemID,
  now: number,
  feature: CurrencyConsumeFeature,
  api: CurrencyReplicantAPI,
): void {
  // Get items data
  const item = getDecorationItem(id);

  // Check if item exist
  if (!item) throw new Error(`Decoration item with ${id} was not found`);

  // Check if item already purchased
  if (state.decoration.purchased[id])
    throw new Error(`Decoration item ${id} was already purchased`);

  // Ignore free items
  if (item.price === 0) {
    return;
  }

  // Consume price
  switch (item.currency) {
    case 'bPoints':
      Inventory.modifiers.consumePoints(state, item.price, feature, api);
      break;
    case 'gems':
      Inventory.modifiers.consumeGems(state, item.price, feature, api);
      break;
    default:
      assertNever(item.currency);
  }

  // Create record about purchase
  addDecorationItem(
    state,
    id,
    Object.keys(tiersItemsMap[id])[0] as DecorationTierID,
    now,
  );
}

/**
 * Equip decoration item
 */
export function equipDecorationItem(
  state: MutableState,
  id: DecorationItemID,
  room: DecorationRoomID,
  slot: DecorationSlot,
  tierId?: DecorationTierID,
): void {
  // Get items data
  const item = getDecorationItem(id);

  // Check if item exist
  if (!item) throw new Error(`Decoration item with ${id} was not found`);

  // Check if room is correct
  if (item.room !== room)
    throw new Error(
      `Decoration item ${id} was tried to be equipped for wrong room`,
    );

  // Check if slot is correct
  if (item.slot !== slot)
    throw new Error(
      `Decoration item ${id} was tried to be equipped for wrong slot`,
    );

  // Add item to selected list
  state.decoration.selected.push({
    id,
    room,
    slot,
    tierId,
  });
}

/**
 * Reset all selected items to default for room
 */
export function unequipAllDecorationItems(
  state: MutableState,
  room: DecorationRoomID,
): void {
  state.decoration.selected = state.decoration.selected.filter(
    (item) => item.room !== room,
  );
}

/**
 * Unequip decoration item.
 */
function unequipDecorationItem(state: MutableState, id: DecorationItemID) {
  state.decoration.selected = state.decoration.selected.filter(
    (item) => item.id !== id,
  );
}
/**
 * Add tier to decoration item. If tier already exist, it will be updated. Won't create new item.
 * @param state
 * @param id
 * @param tier
 * @param now
 * @param quantity
 * @returns
 */
export function addDecorationItemTier(
  state: MutableState,
  id: DecorationItemID,
  tier: DecorationTierID,
  now: number,
  quantity: number = 1,
): void {
  const item = state.decoration.purchased[id];
  if (!item) {
    throw new Error(`Player does not own decoration item ${id}`);
  }

  const tierData = item.tier[tier];
  const tierConfig = getDecorationItemTier(id, tier);

  // New item.
  if (!tierData) {
    item.tier[tier] = {
      tier: tierConfig.tier,
      createdAt: now,
      updatedAt: now,
      isNew: true,
      quantity,
    };

    return;
  }

  // Update existing item
  tierData.quantity += quantity;
  tierData.updatedAt = now;
}

/**
 * Add decoration item to purchased list. And/or add tier to existing item.
 * @param state
 * @param id
 * @param tier
 * @param now
 */
export function addDecorationItem(
  state: MutableState,
  id: DecorationItemID,
  tier: DecorationTierID,
  now: number,
): void {
  const item = state.decoration.purchased[id];

  if (!item) {
    state.decoration.purchased[id] = {
      purchasedAt: now,
      isNew: true,
      tier: {},
    };
  }

  addDecorationItemTier(state, id, tier, now);
}

/**
 * Mark all decoration items and tiers as seen.
 * @param state
 */
export function markDecorationItemsAsSeen(state: MutableState): void {
  Object.values(state.decoration.purchased).forEach((item) => {
    item.isNew = false;
    Object.values(item.tier).forEach((tier) => {
      tier.isNew = false;
    });
  });
}

/**
 * Remove tier from decoration item. If tier quantity is 0, it will be removed.
 */
export function removeDecorationItemTier(
  state: MutableState,
  id: DecorationItemID,
  tier: DecorationTierID,
  quantity: number = 1,
): void {
  const item = state.decoration.purchased[id];
  if (!item) {
    throw new Error(`Player does not own decoration item ${id}`);
  }

  const tierData = item.tier[tier];
  if (!tierData) {
    throw new Error(`Player does not own decoration item ${id} tier ${tier}`);
  }

  tierData.quantity -= quantity;

  // Do not remove tier if quantity is more than 0.
  if (tierData.quantity > 0) return;

  // If item is equipped, unequip it.
  if (getSelectedItemByID(state, id)) {
    unequipDecorationItem(state, id);
  }

  // Remove tier
  delete item.tier[tier];

  // Remove item if no tiers left.
  if (Object.keys(item.tier).length === 0) {
    delete state.decoration.purchased[id];
  }
}
